/**
 * Client side of the signup human check (see apps/api/src/lib/human-check.ts).
 *
 * Flow: prefetch a signed challenge when the signup form mounts; at submit,
 * refresh it if stale (> 14 min), wait out the minimum age (server rejects
 * submissions younger than 3s), then brute-force a nonce whose
 * SHA-256("{challenge}.{lowercased email}.{nonce}") has >= difficulty leading
 * zero bits (~8k hashes at difficulty 13, sub-second via WebCrypto). The
 * result rides to Cognito as validationData and is verified in the PreSignUp
 * trigger. Nothing here is a secret; the signature keeps the server stateless.
 */

const API_URL = import.meta.env.VITE_API_URL ?? '/api';

// Age is measured from when WE received the challenge (client clock), slightly
// above the server's 3s floor so clock skew and latency can't undershoot it.
const MIN_AGE_MS = 3_200;
const STALE_MS = 14 * 60_000;
const MAX_ITERATIONS = 2_000_000; // ~2^21 hashes; difficulty 13 needs ~2^13

interface Challenge {
  challenge: string;
  issuedAt: number;
  difficulty: number;
  sig: string;
  fetchedAt: number; // client clock, ms
}

let cached: Challenge | null = null;

async function fetchChallenge(): Promise<Challenge> {
  const res = await fetch(`${API_URL}/auth/challenge`);
  if (!res.ok) throw new Error(`challenge fetch failed: ${res.status}`);
  const body = await res.json();
  return { ...body, fetchedAt: Date.now() };
}

/** Fire-and-forget warm-up; call when the signup form mounts. */
export function prefetchChallenge(): void {
  void fetchChallenge().then((ch) => { cached = ch; }).catch(() => {});
}

/** Drop the cached challenge so the next attempt fetches a fresh one. */
export function clearChallenge(): void {
  cached = null;
}

function leadingZeroBits(digest: Uint8Array): number {
  let bits = 0;
  for (const byte of digest) {
    if (byte === 0) { bits += 8; continue; }
    bits += Math.clz32(byte) - 24;
    break;
  }
  return bits;
}

async function solveNonce(challenge: string, email: string, difficulty: number): Promise<string> {
  const encoder = new TextEncoder();
  for (let nonce = 0; nonce < MAX_ITERATIONS; nonce++) {
    const digest = new Uint8Array(
      await crypto.subtle.digest('SHA-256', encoder.encode(`${challenge}.${email}.${nonce}`)),
    );
    if (leadingZeroBits(digest) >= difficulty) return String(nonce);
  }
  throw new Error('proof-of-work exhausted');
}

/**
 * Produce the humanCheck validationData JSON for a signup attempt. Waits out
 * the minimum challenge age if the user submitted very fast, so total added
 * latency is at most ~3.2s minus however long the form took to fill.
 */
export async function buildHumanCheck(email: string): Promise<string> {
  if (!cached || Date.now() - cached.fetchedAt > STALE_MS) cached = await fetchChallenge();
  const ch = cached;

  const age = Date.now() - ch.fetchedAt;
  if (age < MIN_AGE_MS) await new Promise((r) => setTimeout(r, MIN_AGE_MS - age));

  const nonce = await solveNonce(ch.challenge, email.trim().toLowerCase(), ch.difficulty);
  return JSON.stringify({ challenge: ch.challenge, issuedAt: ch.issuedAt, sig: ch.sig, nonce });
}
